import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgencyReviewRequests } from '../hooks';
import type { FunnelRange } from '../api/commandCenterApi';
import { Kpi, RangeSwitch, AnalyticsCard } from '@/modules/reviews/analytics/AnalyticsCards';
import { rateText } from '@/modules/reviews/analytics/format';
import type { ReviewRequestRow } from '@/modules/reviews/analytics/types';
import a from '@/modules/reviews/analytics/analytics.module.css';

const STATUS_LABEL: Record<NonNullable<ReviewRequestRow['textStatus']>, string> = {
  not_started: 'Not set up',
  setup: 'Setting up',
  awaiting_registration: 'Awaiting registration',
  live: 'Live',
  paused: 'Paused',
};

function MethodPill({ row }: { row: ReviewRequestRow }) {
  if (row.method === 'link') return <span className={a.pill_neutral}>Link &amp; QR</span>;
  const tone =
    row.textStatus === 'live' ? a.pill_ok : row.textStatus === 'paused' ? a.pill_bad : a.pill_warn;
  return (
    <span className={tone}>
      Text · {row.textStatus ? STATUS_LABEL[row.textStatus] : 'Not set up'}
    </span>
  );
}

/**
 * Practices → Reviews: how every practice asks for reviews, what it produced and what needs
 * attention. A row opens that practice's Reviews module (its text and QR funnels).
 */
export function ReviewRequestsPanel() {
  const [range, setRange] = useState<FunnelRange>('30d');
  const { data, isLoading, isError } = useAgencyReviewRequests(range);
  const navigate = useNavigate();

  const toolbar = (
    <div className={a.toolbar}>
      <span className={a.eyebrow}>Review requests · last {range}</span>
      <RangeSwitch value={range} onChange={setRange} />
    </div>
  );
  if (isLoading)
    return (
      <div className={a.stack}>
        {toolbar}
        <p className={a.muted}>Loading…</p>
      </div>
    );
  if (isError || !data)
    return (
      <div className={a.stack}>
        {toolbar}
        <p className={a.muted}>Couldn’t load review requests right now.</p>
      </div>
    );
  const t = data.totals;
  return (
    <div className={a.stack}>
      {toolbar}
      <div className={a.kpis}>
        <Kpi
          label="New Google reviews"
          value={t.newReviews}
          sub={`${t.fromText} from texts · ${t.fromQr} from QR`}
        />
        <Kpi
          label="Practices texting"
          value={t.practicesTexting}
          sub={`${t.textingLive} live · ${t.textingAwaitingRegistration} awaiting registration`}
        />
        <Kpi label="Patients texted" value={t.texted} sub={`${rateText(t.replyRate)} replied`} />
        <Kpi label="Need attention" value={t.needAttention} sub="see flags below" />
      </div>
      <AnalyticsCard title="Review requests by practice" sub="Click a practice to open its funnels">
        <div className={a.tableWrap}>
          <table className={a.table}>
            <thead>
              <tr>
                <th>Practice</th>
                <th>Method</th>
                <th className={a.right}>New reviews</th>
                <th className={a.right}>From texts</th>
                <th className={a.right}>From QR</th>
                <th className={a.right}>Reply rate</th>
                <th className={a.right}>Delivered</th>
                <th className={a.right}>Opt-outs</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr
                  key={r.practiceId}
                  className={a.rowLink}
                  onClick={() =>
                    navigate(`/agency/practices/${r.practiceId}?tab=modules&module=reviews`)
                  }
                >
                  <td>
                    <b>{r.practiceName}</b>
                  </td>
                  <td>
                    <MethodPill row={r} />
                  </td>
                  <td className={a.right}>
                    <b>{r.newReviews}</b>
                  </td>
                  <td className={a.right}>{r.fromText}</td>
                  <td className={a.right}>{r.fromQr}</td>
                  <td className={a.right}>{rateText(r.replyRate)}</td>
                  <td className={a.right}>{rateText(r.deliveredRate)}</td>
                  <td className={a.right}>{r.optOutRate === null ? '—' : `${r.optOutRate}%`}</td>
                  <td>
                    {r.flags.length === 0 ? (
                      <span className={a.muted}>–</span>
                    ) : (
                      <span className={a.flags}>
                        {r.flags.map((f) => (
                          <span key={f} className={a.pill_warn}>
                            {f}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={a.note}>
          New reviews are Google reviews matched to a request (checked with each daily Google sync).
          Staff test sessions are excluded.
        </p>
      </AnalyticsCard>
    </div>
  );
}
